import { PersonDto } from "api/risk";
import { LightAction } from "components/ui/LightAction";
import { PenIcon } from "components/icons/Icons";
import { GenericLoading } from "components/ui/Loader";
import { NsKey } from "enums/NsKey";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { Network } from "lucide-react";
import { FC, useMemo } from "react";
import { getEligibilityView } from "./getEligibilityView";
import { LegalProtectionCompanyCard } from "./LegalProtectionCompanyCard";
import { LegalProtectionEligibilityBanner } from "./LegalProtectionEligibilityBanner";
import { LegalProtectionSection } from "./LegalProtectionSection";
import { LegalProtectionSubsidiaryCard } from "./LegalProtectionSubsidiaryCard";
import { LP_TEXT } from "./legalProtectionTheme";
import { useLegalProtectionCompanyData } from "./useLegalProtectionCompanyData";
import { useRiskBeneficiaries } from "./useRiskBeneficiaries";

interface LegalProtectionSummaryPanelProps {
  /** Company of the folder. */
  company: PersonDto;
  /** Folder the company is registered on. */
  riskId: string;
  /** Goes back to the company step to change something; without it the panel offers no edition. */
  onEditStep?: () => void;
}

/**
 * Read-only summary of a legal protection folder, shown in a side panel from the pricing page and
 * from the contractualisation: the company with its accounting data and the beneficiaries of the
 * folder, its subsidiaries, and the cumulated figures with the eligibility verdict. Nothing is edited here: the company step owns
 * the edition, since every change recomputes the eligibility.
 */
export const LegalProtectionSummaryPanel: FC<LegalProtectionSummaryPanelProps> = ({ company, riskId, onEditStep }) => {
  const { t } = useDynamicTranslation([NsKey.Common]);
  const { financials, subsidiaries, eligibility, isLoading, eligibilityError, refresh } =
    useLegalProtectionCompanyData(riskId, company.id);
  const { beneficiaries } = useRiskBeneficiaries(riskId);
  const view = useMemo(() => (eligibility ? getEligibilityView(eligibility) : null), [eligibility]);

  if (isLoading) {
    return (
      <div className="relative min-h-[200px]">
        <GenericLoading loading isParentSized />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <LegalProtectionCompanyCard company={company} financials={financials} />
        {beneficiaries.map(beneficiary => (
          <p key={beneficiary.personId} className={`text-sm ${LP_TEXT.body}`}>
            {t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.BENEFICIARY", { name: beneficiary.displayName })}
          </p>
        ))}
      </div>

      <LegalProtectionSection
        icon={<Network className="h-8 w-8 text-primary" />}
        title={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARIES")}
        count={subsidiaries.length}
      >
        {subsidiaries.length > 0
          ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {subsidiaries.map(subsidiary => (
                <LegalProtectionSubsidiaryCard key={subsidiary.id} subsidiary={subsidiary} />
              ))}
            </div>
          )
          : <p className="text-sm text-gray-500">{t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUMMARY.NO_SUBSIDIARY")}</p>}
      </LegalProtectionSection>

      <LegalProtectionEligibilityBanner view={view} hasError={eligibilityError} onRetry={refresh} />

      {onEditStep && (
        <div className="flex justify-end">
          <LightAction
            icon={PenIcon}
            label={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUMMARY.EDIT_STEP")}
            onClick={onEditStep}
          />
        </div>
      )}
    </div>
  );
};
